import { useState, type ReactNode } from 'react';
import { Check, ChevronDown, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';

type PanelKey = 'http' | 'ios' | 'android' | 'flutter' | 'react-native';

interface SdkGuide {
  value: PanelKey;
  label: string;
  init: string;
  signup: string;
  appOpen: string;
}

const NATIVE_PLATFORMS: SdkGuide[] = [
  {
    value: 'ios',
    label: 'iOS (Swift)',
    init: `// 1. Add the SDK via Swift Package Manager
//    File → Add Packages → https://github.com/pulse-app/pulse-sdk-swift

import PulseSDK

// 2. Initialize once at launch (e.g. AppDelegate.didFinishLaunching)
PulseSDK.start(publishableKey: "pk_live_…")
// bundle id is auto-detected from Bundle.main.bundleIdentifier`,
    signup: `// Call right after your user finishes registration.
let result = try await PulseSDK.matchSignup(userId: user.id)

if result.matched {
    // Use result.attribution.c / .ref / .userId / .itemId
    // to credit a referrer, deep-link to a specific item, etc.
}`,
    appOpen: `// Optional — call on every cold launch for re-engagement / DAU.
// Throttled server-side to 1/day per device.
PulseSDK.trackAppOpen(userId: user.id)`,
  },
  {
    value: 'android',
    label: 'Android (Kotlin)',
    init: `// 1. Add the dependency in your app-level build.gradle.kts
dependencies {
    implementation("app.pulse:pulse-sdk-android:1.0.0")
}

// 2. Initialize once in your Application.onCreate
import app.pulse.sdk.PulseSDK
import app.pulse.sdk.PulseConfig

class MyApp : Application() {
    override fun onCreate() {
        super.onCreate()
        PulseSDK.init(
            context = this,
            config = PulseConfig(publishableKey = "pk_live_…"),
        )
        // bundle id is auto-detected from context.packageName
        // Play Install Referrer is read + reported automatically on first launch
    }
}`,
    signup: `// Call right after your user finishes registration.
val result = PulseSDK.matchSignup(userId = user.id)

if (result.matched) {
    // Use result.attribution.c / .ref / .userId / .itemId
    // to credit a referrer, deep-link to a specific item, etc.
}`,
    appOpen: `// Optional — call on every cold launch for re-engagement / DAU.
// Throttled server-side to 1/day per device.
PulseSDK.trackAppOpen(userId = user.id)`,
  },
  {
    value: 'flutter',
    label: 'Flutter (Dart)',
    init: `# 1. Add the package
flutter pub add pulse_sdk

# 2. Initialize once before runApp
import 'package:pulse_sdk/pulse_sdk.dart';

Future<void> main() async {
  WidgetsFlutterBinding.ensureInitialized();
  await PulseSDK.init(publishableKey: 'pk_live_…');
  runApp(const MyApp());
}`,
    signup: `// Call right after your user finishes registration.
final result = await PulseSDK.matchSignup(userId: user.id);

if (result.matched) {
  // Use result.attribution.c / .ref / .userId / .itemId
  // to credit a referrer, deep-link to a specific item, etc.
}`,
    appOpen: `// Optional — call on every cold launch for re-engagement / DAU.
// Throttled server-side to 1/day per device.
PulseSDK.trackAppOpen(userId: user.id);`,
  },
  {
    value: 'react-native',
    label: 'React Native (TypeScript)',
    init: `# 1. Install the package
npm install @pulse/sdk-react-native
# iOS — install pods:
cd ios && pod install && cd ..

# 2. Initialize once at app start
import { PulseSDK } from '@pulse/sdk-react-native';

PulseSDK.init({ publishableKey: 'pk_live_…' });`,
    signup: `// Call right after your user finishes registration.
const result = await PulseSDK.matchSignup({ userId: user.id });

if (result.matched) {
  // Use result.attribution.c / .ref / .userId / .itemId
  // to credit a referrer, deep-link to a specific item, etc.
}`,
    appOpen: `// Optional — call on every cold launch for re-engagement / DAU.
// Throttled server-side to 1/day per device.
PulseSDK.trackAppOpen({ userId: user.id });`,
  },
];

const HTTP_SIGNUP_MATCH = `curl -X POST https://api.plsl.app/v1/sdk/signup-match \\
  -H "Authorization: Bearer pk_live_…" \\
  -H "X-Bundle: com.acme.shop" \\
  -H "X-Device-Id: 7c1b…-d4a9" \\
  -H "Content-Type: application/json" \\
  -d '{ "user_id": 42 }'

# Matched:
# { "matched": true, "attribution": { "c": "k7m3xs", "itemId": 12345 } }
#
# Unmatched (no click within 60 min on this device / IP):
# { "matched": false }`;

const HTTP_APP_OPEN = `curl -X POST https://api.plsl.app/v1/sdk/app-open \\
  -H "Authorization: Bearer pk_live_…" \\
  -H "X-Bundle: com.acme.shop" \\
  -H "X-Device-Id: 7c1b…-d4a9" \\
  -H "Content-Type: application/json" \\
  -d '{ "user_id": 42 }'`;

const HTTP_ANDROID_REFERRER = `curl -X POST https://api.plsl.app/v1/sdk/android-referrer \\
  -H "Authorization: Bearer pk_live_…" \\
  -H "X-Bundle: com.acme.shop" \\
  -H "X-Device-Id: 7c1b…-d4a9" \\
  -H "Content-Type: application/json" \\
  -d '{
    "referrer": "<raw query string from Play Install Referrer>",
    "install_begin_ts": 1700000000,
    "referrer_click_ts": 1700000000
  }'`;

const SECRET_KEY_EXAMPLE = `# Server-side only — never embed in mobile or browser code.

# List your apps
curl https://api.plsl.app/v1/apps \\
  -H "Authorization: Bearer sk_live_…"

# Create a campaign (server generates the opaque code)
curl -X POST https://api.plsl.app/v1/campaigns \\
  -H "Authorization: Bearer sk_live_…" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Spring IG Story",
    "appId": 1,
    "type": "redirect",
    "source": "instagram"
  }'`;

export function IntegrationGuide() {
  const [open, setOpen] = useState<PanelKey | null>('http');

  function toggle(key: PanelKey) {
    setOpen((curr) => (curr === key ? null : key));
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Integrate Pulse in your app</CardTitle>
        <CardDescription>
          Every integration is plain HTTP + JSON — start with the panel
          below. The native SDKs are optional convenience wrappers and
          are <strong>recommended on Android</strong>, where they read
          the Play Install Referrer for deterministic (~100% accurate)
          attribution. Without them, Android falls back to IP matching
          like iOS (~50–70%).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Accordion
          label="HTTP (any language)"
          open={open === 'http'}
          onToggle={() => toggle('http')}
        >
          <HttpPanel />
        </Accordion>

        <div className="space-y-2">
          <div className="text-sm font-medium">
            Native SDK convenience wrappers (optional)
          </div>
          <p className="text-sm text-muted-foreground">
            Thin wrappers around the HTTP API that handle the
            device-id and (on Android) read the Play Install Referrer
            for you. Packages aren't published yet — the snippets
            below show the planned shape.
          </p>
          <div className="space-y-2 pt-1">
            {NATIVE_PLATFORMS.map((p) => (
              <Accordion
                key={p.value}
                label={p.label}
                comingSoon
                open={open === p.value}
                onToggle={() => toggle(p.value)}
              >
                <SdkPanel guide={p} />
              </Accordion>
            ))}
          </div>
        </div>

        <div className="space-y-2 rounded-3xl bg-muted/40 p-4">
          <div className="text-sm font-medium">
            Server-side with the secret key
          </div>
          <p className="text-sm text-muted-foreground">
            Use <code className="font-mono">sk_live_…</code> from your
            backend to manage apps, campaigns, and read analytics.
            Full read/write tenant scope — never ship this to a
            client.
          </p>
          <CodeBlock code={SECRET_KEY_EXAMPLE} />
        </div>
      </CardContent>
    </Card>
  );
}

function HttpPanel() {
  return (
    <div className="space-y-5">
      <Step
        n={1}
        title="What your app needs to send"
        description="Generate a UUID v4 the first time your app launches and persist it (UserDefaults / SharedPreferences / equivalent). Send it on every Pulse call together with your publishable key and bundle id."
      >
        <div className="overflow-hidden rounded-2xl ring-1 ring-foreground/5 dark:ring-foreground/10">
          <table className="w-full text-xs">
            <tbody className="font-mono [&_td]:px-3 [&_td]:py-2 [&_tr:not(:last-child)]:border-b [&_tr]:border-foreground/5">
              <tr>
                <td className="w-44 text-muted-foreground">Authorization</td>
                <td>Bearer pk_live_…</td>
              </tr>
              <tr>
                <td className="text-muted-foreground">X-Bundle</td>
                <td>com.acme.shop (your declared package / bundle id)</td>
              </tr>
              <tr>
                <td className="text-muted-foreground">X-Device-Id</td>
                <td>UUID v4 — same value across calls from the same device</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="space-y-2 rounded-2xl bg-amber-500/10 p-3 text-sm text-amber-900 ring-1 ring-amber-500/20 dark:text-amber-100 dark:ring-amber-400/30">
          <div className="font-medium">
            Android only — deterministic attribution
          </div>
          <p>
            To get ~100% match accuracy on Android, have your Android
            client read the Play Install Referrer via Google's{' '}
            <code className="font-mono">com.android.installreferrer</code>{' '}
            library on first cold launch and POST it to{' '}
            <code className="font-mono">/v1/sdk/android-referrer</code>.
            Skip this and Android matches the same way iOS does
            (server-side by IP within 60 min).
          </p>
          <CodeBlock code={HTTP_ANDROID_REFERRER} />
        </div>
      </Step>

      <Step
        n={2}
        title="Match attribution at signup"
        description="When your user finishes registration, ask Pulse to match them to a click. Android matches deterministically by device-id (if the referrer was reported); iOS matches by IP within 60 minutes."
      >
        <CodeBlock code={HTTP_SIGNUP_MATCH} />
      </Step>

      <Step
        n={3}
        title="Track app opens (optional)"
        description="Useful for re-engagement and DAU. Throttled to one event per device per day."
      >
        <CodeBlock code={HTTP_APP_OPEN} />
      </Step>
    </div>
  );
}

function SdkPanel({ guide }: { guide: SdkGuide }) {
  return (
    <div className="space-y-5">
      <Step
        n={1}
        title="Install and initialize the SDK"
        description="The SDK generates a stable device-id on first launch and (on Android) reports the Play Install Referrer to Pulse."
      >
        <CodeBlock code={guide.init} />
      </Step>
      <Step
        n={2}
        title="Match attribution at signup"
        description="When your user finishes registration, ask Pulse to match them to a click."
      >
        <CodeBlock code={guide.signup} />
      </Step>
      <Step
        n={3}
        title="Track app opens (optional)"
        description="Useful for re-engagement and DAU. Throttled to one event per device per day."
      >
        <CodeBlock code={guide.appOpen} />
      </Step>
    </div>
  );
}

function Accordion({
  label,
  comingSoon,
  open,
  onToggle,
  children,
}: {
  label: string;
  comingSoon?: boolean;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-3xl ring-1 ring-foreground/5 dark:ring-foreground/10',
        open && 'bg-muted/20',
      )}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-medium transition-colors hover:bg-muted/30"
      >
        <span className="flex items-center gap-2">
          {label}
          {comingSoon && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              Coming soon
            </span>
          )}
        </span>
        <ChevronDown
          className={cn(
            'size-4 text-muted-foreground transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

function Step({
  n,
  title,
  description,
  children,
}: {
  n: number;
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-2">
      <div className="flex items-baseline gap-2">
        <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
          {n}
        </span>
        <h3 className="font-heading text-sm font-medium">{title}</h3>
      </div>
      <p className="pl-8 text-sm text-muted-foreground">{description}</p>
      <div className="space-y-3 pl-8">{children}</div>
    </section>
  );
}

function CodeBlock({ code, className }: { code: string; className?: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' });
    }
  }

  return (
    <div className={cn('group/code relative', className)}>
      <pre className="overflow-x-auto rounded-2xl bg-muted/60 p-3 pr-12 font-mono text-xs leading-relaxed text-foreground">
        <code>{code}</code>
      </pre>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className="absolute right-1.5 top-1.5 opacity-0 transition-opacity group-hover/code:opacity-100 focus-visible:opacity-100"
        onClick={copy}
        aria-label={copied ? 'Copied' : 'Copy code'}
      >
        {copied ? <Check /> : <Copy />}
      </Button>
    </div>
  );
}
